import { useEffect, useState } from "react";
import type { MeDto } from "../../types/index.ts";
import { limitLock } from "./utils.ts";
import type { LimitLock } from "./types.ts";

const MAX_TIMEOUT = 2 ** 31 - 1;

/**
 * The lock for this page. When the reset passes it asks the server again, so the page opens
 * without a reload, and renders again even if that answer is slow or fails.
 */
export function useLimitLock(me: MeDto | null, refused: boolean, refreshMe: () => void): LimitLock {
  const [now, setNow] = useState(() => Date.now());
  const lock = limitLock(me, refused, now);
  const resetsAt = lock.locked ? lock.resetsAt : null;

  // A month is longer than setTimeout can wait, so a long wait is taken in steps and only the
  // last one asks the server.
  useEffect(() => {
    if (resetsAt === null) return;
    const wait = resetsAt - Date.now();
    const timer = setTimeout(() => {
      setNow(Date.now());
      if (wait <= MAX_TIMEOUT) refreshMe();
    }, Math.min(Math.max(0, wait), MAX_TIMEOUT));
    return () => clearTimeout(timer);
  }, [resetsAt, refreshMe, now]);

  return lock;
}
